export const MAX_PROMO_CODE_LENGTH = 64;
export const MAX_PROMO_CHECKOUT_INSTRUCTION_LENGTH = 500;

export const PROMO_ERROR_CODES = {
  INVALID: "PROMO_CODE_INVALID",
  NOT_FOUND: "PROMO_CODE_NOT_FOUND",
  INVALID_FORMAT: "PROMO_CODE_INVALID_FORMAT",
  INACTIVE: "PROMO_CODE_INACTIVE",
  EXHAUSTED: "PROMO_CODE_EXHAUSTED",
  ALREADY_USED: "PROMO_CODE_ALREADY_USED",
  NEW_CUSTOMERS_ONLY: "PROMO_CODE_NEW_CUSTOMERS_ONLY",
  EMAIL_DOMAIN_NOT_ALLOWED: "PROMO_CODE_EMAIL_DOMAIN_NOT_ALLOWED",
  RATE_LIMITED: "PROMO_CODE_RATE_LIMITED",
  QUOTE_CHANGED: "PROMO_QUOTE_CHANGED",
};

export const SUBSCRIPTION_PROMO_TERM_VARIANTS = {
  AFFILIATE_MONTHLY: "AFFILIATE_MONTHLY",
  FREE_VERIFICATION: "FREE_VERIFICATION",
  PARTIAL_REGULAR: "PARTIAL_REGULAR",
  LEGACY: "LEGACY",
};

const KNOWN_PROMO_ERROR_CODES = new Set(Object.values(PROMO_ERROR_CODES));
const PROMO_QUOTE_FINGERPRINT_PATTERN = /^[a-f0-9]{64}$/;

const asFiniteNumber = (value, fallback = null) => {
  if (value === undefined) {
    return fallback;
  }
  if (
    (typeof value !== "number" && typeof value !== "string") ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const asOptionalBoolean = (value) =>
  typeof value === "boolean" ? value : undefined;

const isIntegerValue = (value) =>
  (typeof value === "number" ||
    (typeof value === "string" && value.trim() !== "")) &&
  Number.isInteger(Number(value));

const isPositiveInteger = (value) =>
  isIntegerValue(value) && Number(value) > 0;

const isNonNegativeInteger = (value) =>
  isIntegerValue(value) && Number(value) >= 0;

export const hasSubscriptionPromoRenewalTerms = (terms) =>
  isNonNegativeInteger(terms?.renewalAmount) &&
  isPositiveInteger(terms?.renewalDurationDays);

export const classifySubscriptionPromoTerms = (terms) => {
  const affiliate = asOptionalBoolean(terms?.affiliate);
  const cardVerification = asOptionalBoolean(terms?.cardVerification);
  const autoRenew = asOptionalBoolean(terms?.autoRenew);
  const initialAmount = terms?.initialAmount;
  const durationDays = terms?.durationDays;
  const discountPercent = terms?.discountPercent;
  const hasExplicitClassification =
    affiliate !== undefined &&
    cardVerification !== undefined &&
    autoRenew !== undefined;

  if (!hasExplicitClassification || !isPositiveInteger(durationDays)) {
    return SUBSCRIPTION_PROMO_TERM_VARIANTS.LEGACY;
  }

  if (
    affiliate &&
    !cardVerification &&
    isPositiveInteger(initialAmount) &&
    isIntegerValue(discountPercent) &&
    Number(discountPercent) >= 1 &&
    Number(discountPercent) < 100
  ) {
    return SUBSCRIPTION_PROMO_TERM_VARIANTS.AFFILIATE_MONTHLY;
  }

  if (
    !affiliate &&
    cardVerification &&
    isNonNegativeInteger(initialAmount) &&
    Number(initialAmount) === 0 &&
    isIntegerValue(discountPercent) &&
    Number(discountPercent) === 100
  ) {
    return SUBSCRIPTION_PROMO_TERM_VARIANTS.FREE_VERIFICATION;
  }

  if (
    !affiliate &&
    !cardVerification &&
    isPositiveInteger(initialAmount) &&
    isIntegerValue(discountPercent) &&
    Number(discountPercent) >= 1 &&
    Number(discountPercent) < 100
  ) {
    return SUBSCRIPTION_PROMO_TERM_VARIANTS.PARTIAL_REGULAR;
  }

  return SUBSCRIPTION_PROMO_TERM_VARIANTS.LEGACY;
};

const hasUnsupportedInstructionControl = (value) =>
  Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0);
    return (
      codePoint === 0x7f ||
      (codePoint <= 0x1f &&
        codePoint !== 0x09 &&
        codePoint !== 0x0a &&
        codePoint !== 0x0d)
    );
  });

export const normalizePromoCode = (value = "") =>
  String(value)
    .trim()
    .toUpperCase()
    .slice(0, MAX_PROMO_CODE_LENGTH);

export const isValidPromoQuoteFingerprint = (value) =>
  typeof value === "string" && PROMO_QUOTE_FINGERPRINT_PATTERN.test(value);

export const adaptSubscriptionPromoQuote = (
  rawQuote,
  { authenticated = false } = {}
) => {
  const code = normalizePromoCode(rawQuote?.code);
  const durationDays = asFiniteNumber(rawQuote?.durationDays);
  const rawQuoteFingerprint = rawQuote?.quoteFingerprint;
  const renewalAmount = asFiniteNumber(rawQuote?.renewalAmount);
  const discountPercent = asFiniteNumber(rawQuote?.discountPercent, 100);
  const initialAmount = asFiniteNumber(rawQuote?.initialAmount, 0);
  const parsedRenewalDurationDays = asFiniteNumber(
    rawQuote?.renewalDurationDays
  );
  const renewalDurationDays =
    parsedRenewalDurationDays !== null && parsedRenewalDurationDays > 0
      ? parsedRenewalDurationDays
      : null;
  const rawCheckoutInstruction = rawQuote?.checkoutInstruction;
  const rawCheckoutInstructionVersion = rawQuote?.checkoutInstructionVersion;
  const affiliate = asOptionalBoolean(rawQuote?.affiliate);
  const cardVerification = asOptionalBoolean(rawQuote?.cardVerification);
  const autoRenew = asOptionalBoolean(rawQuote?.autoRenew);
  const hasMalformedCommercialFlag = [
    rawQuote?.affiliate,
    rawQuote?.cardVerification,
    rawQuote?.autoRenew,
  ].some(
    (value) =>
      value !== undefined && value !== null && typeof value !== "boolean"
  );
  const hasCompleteCommercialFlags =
    affiliate !== undefined &&
    cardVerification !== undefined &&
    autoRenew !== undefined;
  const checkoutInstruction =
    typeof rawCheckoutInstruction === "string"
      ? rawCheckoutInstruction.replace(/\r\n?/g, "\n").trim() || null
      : rawCheckoutInstruction === null || rawCheckoutInstruction === undefined
        ? null
        : undefined;
  const checkoutInstructionVersion =
    rawCheckoutInstructionVersion === null ||
    rawCheckoutInstructionVersion === undefined
      ? null
      : asFiniteNumber(rawCheckoutInstructionVersion);

  if (
    !code ||
    !Number.isInteger(durationDays) ||
    durationDays <= 0 ||
    renewalAmount === null ||
    renewalAmount < 0 ||
    !Number.isInteger(discountPercent) ||
    discountPercent < 1 ||
    discountPercent > 100 ||
    !Number.isInteger(initialAmount) ||
    initialAmount < 0 ||
    initialAmount > renewalAmount ||
    initialAmount !==
      Math.round((renewalAmount * (100 - discountPercent)) / 100) ||
    renewalDurationDays === null ||
    hasMalformedCommercialFlag ||
    (hasCompleteCommercialFlags &&
      (!autoRenew ||
        cardVerification !== (initialAmount === 0) ||
        (affiliate && (cardVerification || discountPercent === 100)))) ||
    checkoutInstruction === undefined ||
    (checkoutInstruction !== null &&
      (!Number.isInteger(checkoutInstructionVersion) ||
        checkoutInstructionVersion <= 0)) ||
    (checkoutInstruction === null && checkoutInstructionVersion !== null) ||
    (checkoutInstruction !== null &&
      (checkoutInstruction.length > MAX_PROMO_CHECKOUT_INSTRUCTION_LENGTH ||
        hasUnsupportedInstructionControl(checkoutInstruction))) ||
    (rawQuoteFingerprint !== null &&
      rawQuoteFingerprint !== undefined &&
      !isValidPromoQuoteFingerprint(rawQuoteFingerprint))
  ) {
    const error = new Error("Invalid promo quote response");
    error.code = "PROMO_QUOTE_INVALID_RESPONSE";
    throw error;
  }

  const currency = /^[A-Z]{3}$/.test(String(rawQuote?.currency || ""))
    ? rawQuote.currency
    : "RSD";

  return {
    code,
    currency,
    durationDays,
    discountPercent,
    initialAmount,
    renewalAmount,
    renewalDurationDays,
    affiliate,
    cardVerification,
    autoRenew,
    quoteFingerprint: rawQuoteFingerprint ?? null,
    checkoutInstruction,
    checkoutInstructionVersion,
    provisional:
      asOptionalBoolean(rawQuote?.provisional) ?? !authenticated,
  };
};

export const getPromoErrorCode = (error) => {
  const candidates = [
    error?.response?.data?.reasonCode,
    error?.response?.data?.errorCode,
    error?.response?.data?.code,
    error?.code,
  ];

  for (const candidate of candidates) {
    if (KNOWN_PROMO_ERROR_CODES.has(candidate)) {
      return candidate;
    }
  }

  return null;
};

export const isPromoEligibilityError = (error) =>
  Boolean(getPromoErrorCode(error));

export const getPromoErrorTranslationKey = (
  error,
  { missingEmail = false } = {}
) => {
  if (!error?.response) {
    return "subscription.promo.errors.network";
  }

  switch (getPromoErrorCode(error)) {
    case PROMO_ERROR_CODES.NOT_FOUND:
    case PROMO_ERROR_CODES.INVALID_FORMAT:
      return "subscription.promo.errors.notRecognized";
    case PROMO_ERROR_CODES.INACTIVE:
      return "subscription.promo.errors.inactive";
    case PROMO_ERROR_CODES.EXHAUSTED:
      return "subscription.promo.errors.exhausted";
    case PROMO_ERROR_CODES.ALREADY_USED:
      return "subscription.promo.errors.alreadyUsed";
    case PROMO_ERROR_CODES.NEW_CUSTOMERS_ONLY:
      return "subscription.promo.errors.newCustomersOnly";
    case PROMO_ERROR_CODES.EMAIL_DOMAIN_NOT_ALLOWED:
      return missingEmail
        ? "subscription.promo.errors.emailRequiredOrInvalid"
        : "subscription.promo.errors.emailDomainNotAllowed";
    case PROMO_ERROR_CODES.RATE_LIMITED:
      return "subscription.promo.errors.rateLimited";
    case PROMO_ERROR_CODES.QUOTE_CHANGED:
      return "subscription.promo.errors.quoteChanged";
    case PROMO_ERROR_CODES.INVALID:
      return "subscription.promo.errors.invalid";
    default:
      return "subscription.promo.errors.unknown";
  }
};

export const formatPromoCurrency = (
  amount,
  currency = "RSD",
  language = "sr"
) => {
  const locale = language?.toLowerCase().startsWith("en") ? "en-RS" : "sr-RS";
  const normalizedCurrency =
    typeof currency === "string" && /^[a-z]{3}$/i.test(currency.trim())
      ? currency.trim().toUpperCase()
      : "RSD";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: normalizedCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  } catch (_error) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "RSD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  }
};
